/**
 * Feature flags — client-side hook + active-flag registry for error attribution.
 *
 * How it works:
 *   • `useFeatureFlag("my-flag")` returns { enabled, isLoading }. When enabled
 *     resolves to true the flag key is added to the active-flag registry so
 *     any subsequent unhandled error is tagged with it via reportFlagError().
 *   • Anonymous users still get a deterministic bucket (random hash), so
 *     rollout percentages work pre-auth too.
 *   • The kill-switch monitor (see /api/public/hooks/feature-flag-kill-switch)
 *     may flip enabled -> false server-side at any time; this hook re-fetches
 *     every 60s so a killed flag disappears from the UI within a minute
 *     without needing a full page reload.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const REFETCH_MS = 60_000;

/** Keys of every flag currently evaluated `true` for this session. */
const activeFlags = new Set<string>();

/** Called by useFeatureFlag; also exported so error boundaries can read the set. */
export function getActiveFlagKeys(): string[] {
  return Array.from(activeFlags);
}

async function fetchFlag(key: string): Promise<boolean> {
  const { data: userRes } = await supabase.auth.getUser();
  const userId = userRes?.user?.id ?? null;
  const { data, error } = await supabase.rpc("is_feature_flag_enabled_for" as never, {
    _key: key,
    _user_id: userId,
  } as never);
  if (error) {
    // Fail-closed: never render a flag as on when we can't verify.
    console.warn(`[feature-flags] ${key}: rpc failed —`, error.message);
    return false;
  }
  return Boolean(data);
}

export function useFeatureFlag(key: string): { enabled: boolean; isLoading: boolean } {
  const q = useQuery({
    queryKey: ["feature-flag", key],
    queryFn: () => fetchFlag(key),
    staleTime: REFETCH_MS,
    refetchInterval: REFETCH_MS,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (q.data === true) activeFlags.add(key);
    else activeFlags.delete(key);
    return () => {
      activeFlags.delete(key);
    };
  }, [key, q.data]);

  return { enabled: q.data === true, isLoading: q.isLoading };
}

/**
 * Fire-and-forget error reporter. Tags the event with every flag currently
 * evaluated `true` for this session so the kill-switch RPC can attribute a
 * spike to a specific flag. Safe to call from unauthenticated pages.
 */
export function reportFlagError(input: {
  error: unknown;
  route?: string;
}): void {
  try {
    const err = input.error;
    const message =
      err instanceof Error ? err.message : typeof err === "string" ? err : JSON.stringify(err);
    if (!message) return;
    // Cheap deterministic hash for de-dup grouping.
    const hash = hashString(`${message}::${err instanceof Error ? err.stack ?? "" : ""}`);
    const keys = getActiveFlagKeys();
    const route = input.route ?? (typeof window !== "undefined" ? window.location.pathname : "");
    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";

    // Fire-and-forget; never let error reporting itself throw.
    void supabase
      .rpc("record_feature_flag_events" as never, {
        _flag_keys: keys,
        _error_hash: hash,
        _error_message: message.slice(0, 2000),
        _route: route.slice(0, 500),
        _user_agent: ua.slice(0, 500),
      } as never)
      .then(({ error }) => {
        if (error) console.warn("[feature-flags] event report failed:", error.message);
      });
  } catch {
    // Swallow — reporter must never crash the app.
  }
}

function hashString(input: string): string {
  // FNV-1a 32-bit — good enough for grouping, fast, no deps.
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}
